import {
	AuditLogTable,
	BusinessReviewPanel,
	TimeAgo,
} from "@nwga/features/admin/ui";
import { useTRPC } from "@nwga/features/client";
import { Badge } from "@nwga/ui/components/badge";
import { Button } from "@nwga/ui/components/button";
import { EmptyState } from "@nwga/ui/components/empty-state";
import { Skeleton } from "@nwga/ui/components/skeleton";
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@nwga/ui/components/tabs";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BriefcaseIcon } from "lucide-react";
import { ErrorState } from "../../../components/error-state.tsx";

/**
 * Everything about one business, on the page where it is judged.
 *
 * A reviewer deciding whether to approve wants the submission, who is behind
 * it, what they have staged, and what has already been done to them — all
 * without leaving the page. Splitting those across screens is what makes a
 * review take five minutes instead of thirty seconds.
 */
export const Route = createFileRoute("/admin/businesses/$businessId")({
	component: AdminBusinessDetailPage,
	loader: ({ context, params }) =>
		context.queryClient.ensureQueryData(
			context.trpc.businesses.byId.queryOptions({
				businessId: params.businessId,
			}),
		),
});

function AdminBusinessDetailPage() {
	const { businessId } = Route.useParams();
	const trpc = useTRPC();

	const business = useQuery(trpc.businesses.byId.queryOptions({ businessId }));

	const listings = useQuery({
		...trpc.admin.jobs.queryOptions({ businessId, limit: 50 }),
		enabled: business.isSuccess,
	});

	if (business.isPending) {
		return <Skeleton className="h-64 w-full" />;
	}

	if (business.isError) {
		return (
			<ErrorState
				error={business.error}
				onRetry={() => void business.refetch()}
			/>
		);
	}

	return (
		<div className="space-y-6">
			<Button asChild variant="ghost" size="sm" className="-ml-2">
				{/* The businesses list has a tab per status; send the reviewer back to
				the one this business actually lives in, not whatever tab defaults to. */}
				<Link to="/admin/businesses" search={{ tab: business.data.status }}>
					← Businesses
				</Link>
			</Button>

			<BusinessReviewPanel business={business.data} />

			<Tabs defaultValue="listings">
				<TabsList>
					<TabsTrigger value="listings">Listings</TabsTrigger>
					<TabsTrigger value="history">History</TabsTrigger>
				</TabsList>

				<TabsContent value="listings" className="pt-4">
					{listings.isPending && <Skeleton className="h-32 w-full" />}

					{listings.isSuccess && listings.data.items.length === 0 && (
						<EmptyState
							icon={BriefcaseIcon}
							title="No listings"
							description="This business has not posted anything yet."
						/>
					)}

					{listings.isSuccess && listings.data.items.length > 0 && (
						<ul className="space-y-2">
							{listings.data.items.map((job) => (
								<li
									key={job.id}
									className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-4"
								>
									<div className="min-w-0">
										<p className="truncate font-medium">{job.title}</p>
										<p className="text-muted-foreground text-sm">
											<TimeAgo value={job.publishedAt ?? job.createdAt} />
										</p>
									</div>
									<Badge
										variant={job.status === "published" ? "default" : "outline"}
										className="capitalize"
									>
										{job.status}
									</Badge>
								</li>
							))}
						</ul>
					)}
				</TabsContent>

				<TabsContent value="history" className="pt-4">
					{/* Scoped to this business, so the trail reads as its case file. */}
					<AuditLogTable subjectId={businessId} />
				</TabsContent>
			</Tabs>
		</div>
	);
}
